import { UTCDate } from '@date-fns/utc';
import type { ByPhraseFilter, ByValueFilter } from '@openx/components-table/serverSideCriteriaProcessing';
import { expiringSoonStatus } from '@openx/types/segments';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { addDays } from 'date-fns';
import omit from 'lodash/omit';
import type { AudiencesListFilters } from '../types';
import { type MappedNumericFilter, mapNumericFilter } from './mapNumericFilter';

const numericFields = new Set(['addressable_reach']);
const fieldsToDecode = new Set(['direct_audience_provider', 'account', 'createdBy']);

export const adjustCriteriaFilters = (criteria: AllTableCriteria<AudiencesListFilters>) => {
  const hasExpiringSoonStatus = criteria.filters?.status?.includes?.('expiring_soon');
  const now = new UTCDate();
  now.setSeconds(0, 0);

  const initialByValue: ByValueFilter = hasExpiringSoonStatus
    ? [{ field: 'estimated_expiry_date', values: [now.toISOString(), addDays(now, 10).toISOString()] }]
    : [];

  const byValue: ByValueFilter = Object.entries(omit(criteria.filters, 'phrase')).reduce((acc, [field, values]) => {
    const isNumericField = numericFields.has(field);

    if (isNumericField && Array.isArray(values) && values) {
      const mappedValues: MappedNumericFilter = mapNumericFilter(values);

      acc.push({
        field,
        values: mappedValues,
      });

      return acc;
    }

    if (fieldsToDecode.has(field) && Array.isArray(values) && values) {
      const updatedValues = values.map(value => decodeURIComponent(value.trim()));

      acc.push({
        field,
        values: updatedValues,
      });

      return acc;
    }

    if (Array.isArray(values) && values) {
      acc.push({
        field,
        values: values.filter(status => !!status && status !== expiringSoonStatus),
      });
    }

    return acc;
  }, initialByValue);

  const byPhrase: ByPhraseFilter = {
    fields: ['name', 'account', 'direct_audience_provider'],
    phrase: criteria.filters?.phrase ?? '',
  };

  return { byPhrase, byValue };
};
